// ─── ANSI helpers ────────────────────────────────────────────────────────────
const R = '\x1b[0m';
const BOLD = '\x1b[1m';
const DIM = '\x1b[2m';

/** True-colour RGB foreground */
function rgb(r: number, g: number, b: number): string {
  return `\x1b[38;2;${r};${g};${b}m`;
}

// Purple palette (dark → vivid → light)
const P0 = rgb(88, 28, 135);   // deep violet
const P1 = rgb(124, 58, 237);  // violet
const P2 = rgb(139, 92, 246);  // medium purple
const P3 = rgb(167, 139, 250); // lavender
const P4 = rgb(192, 132, 252); // light purple
const P5 = rgb(216, 180, 254); // pale lilac

// ─── Simple Braille Spinner ────────────────────────────────────────────────
export class Spinner {
  private frames = ['⠋', '⠙', '⠹', '⠸', '⠼', '⠴', '⠦', '⠧', '⠇', '⠏'];
  private currentFrame = 0;
  private timer: NodeJS.Timeout | null = null;
  private text: string;
  private color = rgb(168, 85, 247);

  constructor(text: string) {
    this.text = text;
  }

  start(text?: string): this {
    if (text) this.text = text;
    if (this.timer) clearInterval(this.timer);

    process.stdout.write('\x1b[?25l');
    this.timer = setInterval(() => {
      const frame = this.frames[this.currentFrame];
      process.stdout.write(`\r\x1b[K${this.color}${frame}${R} \x1b[97m${this.text}${R}`);
      this.currentFrame = (this.currentFrame + 1) % this.frames.length;
    }, 80);
    return this;
  }

  updateText(text: string): this {
    this.text = text;
    return this;
  }

  succeed(text?: string) {
    this.stop();
    const message = text || this.text;
    console.log(`\r\x1b[K\x1b[1;92m✔${R} \x1b[97m${message}${R}`);
  }

  fail(text?: string) {
    this.stop();
    const message = text || this.text;
    console.log(`\r\x1b[K\x1b[1;91m✖${R} \x1b[97m${message}${R}`);
  }

  info(text?: string) {
    this.stop();
    const message = text || this.text;
    console.log(`\r\x1b[K\x1b[1;96mℹ${R} \x1b[97m${message}${R}`);
  }

  stop() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    process.stdout.write('\x1b[?25h');
  }
}

export function createSpinner(text: string): Spinner {
  return new Spinner(text);
}

// ─── Loading Animation (used while waiting for AI response) ──────────────────

/** Messages that cycle while the AI is thinking */
const LOADING_MESSAGES = [
  'Memproses permintaan...',
  'Menganalisis konteks...',
  'Menghubungi model AI...',
  'Sedang berpikir...',
  'Merangkai jawaban...',
  'Sebentar lagi...',
];

/**
 * Cinematic multi-layer loading animation:
 *  Line 1: gradient "orb" spinner + cycling message  (updates every 120 ms)
 *  Line 2: scrolling gradient bar                   (updates every 60 ms)
 *
 * Uses 2-line in-place redraw so nothing is appended to the scroll buffer.
 */
export class LoadingAnimation {
  // ── Orb frames (dot-matrix orb cycling through purple shades) ──
  private readonly orbFrames: string[] = (() => {
    const raw = ['◉', '◎', '○', '◌', '○', '◎'];
    const colors = [P2, P3, P4, P5, P4, P3];
    return raw.map((c, i) => `${BOLD}${colors[i]}${c}${R}`);
  })();

  // ── Scrolling gradient bar characters ──
  private readonly barChars = ['▏', '▎', '▍', '▌', '▋', '▊', '▉', '█'];
  private readonly barColors = [P0, P1, P2, P3, P4, P5, P4, P3, P2, P1];
  private readonly BAR_WIDTH = 38;

  // ── State ──
  private orbFrame = 0;
  private barOffset = 0;
  private msgIndex = 0;
  private msgCounter = 0;
  private message: string | null = null;
  private readonly MSG_HOLD = 12; // ticks before cycling to next message (at 80ms → ~960ms)

  private fastTimer: NodeJS.Timeout | null = null;
  private slowTimer: NodeJS.Timeout | null = null;
  private stopped = false;
  private linesWritten = 0;

  constructor(initialMessage = 'Menghubungi model AI...') {
    this.message = initialMessage;
  }

  updateText(text: string): this {
    this.message = text;
    return this;
  }

  /** Start the animation. Call stop() when the response begins. */
  start(): this {
    if (this.stopped) return this;
    process.stdout.write('\x1b[?25l'); // hide cursor

    // Initial render
    this._renderOrb();
    this._renderBar();
    this.linesWritten = 2;

    // Orb + message: every 120 ms
    this.slowTimer = setInterval(() => {
      if (this.stopped) return;
      this._moveUp(this.linesWritten);
      this._renderOrb();
      this._renderBar();
      this.linesWritten = 2;
    }, 120);

    return this;
  }

  /** Stop the animation and clear both lines. */
  stop(): void {
    if (this.stopped) return;
    this.stopped = true;

    if (this.slowTimer) { clearInterval(this.slowTimer); this.slowTimer = null; }
    if (this.fastTimer) { clearInterval(this.fastTimer); this.fastTimer = null; }

    // Erase both lines
    this._moveUp(this.linesWritten);
    for (let i = 0; i < this.linesWritten; i++) {
      process.stdout.write('\r\x1b[K');
      if (i < this.linesWritten - 1) process.stdout.write('\n');
    }
    // Move back to top of the cleared block
    if (this.linesWritten > 1) {
      process.stdout.write(`\x1b[${this.linesWritten - 1}A`);
    }
    process.stdout.write('\x1b[?25h'); // restore cursor
  }

  // ── Private renderers ─────────────────────────────────────────────────────

  private _moveUp(lines: number): void {
    if (lines > 0) process.stdout.write(`\x1b[${lines}A`);
  }

  private _renderOrb(): void {
    const orb = this.orbFrames[this.orbFrame % this.orbFrames.length]!;
    this.orbFrame++;

    const msg = this.message ?? LOADING_MESSAGES[this.msgIndex % LOADING_MESSAGES.length]!;
    if (this.message === null) {
      this.msgCounter++;
      if (this.msgCounter >= this.MSG_HOLD) {
        this.msgCounter = 0;
        this.msgIndex++;
      }
    }

    // Dim bracket decorations in muted violet
    const lbracket = `${DIM}${P1}❮${R}`;
    const rbracket = `${DIM}${P1}❯${R}`;

    process.stdout.write(`\r\x1b[K ${orb}  ${lbracket} ${P3}${msg}${R} ${rbracket}\n`);
  }

  private _renderBar(): void {
    let bar = '';
    for (let i = 0; i < this.BAR_WIDTH; i++) {
      const colorIdx = (i + this.barOffset) % this.barColors.length;
      const charIdx = Math.floor(
        ((Math.sin((i + this.barOffset) * 0.5) + 1) / 2) * (this.barChars.length - 1),
      );
      bar += `${this.barColors[colorIdx]}${this.barChars[charIdx]}`;
    }
    this.barOffset++;

    const edge = `${DIM}${P1}`;
    process.stdout.write(`\r\x1b[K  ${edge}╠${R}${bar}${R}${edge}╣${R}\n`);
  }
}

export function createLoadingAnimation(initialMessage?: string): LoadingAnimation {
  return new LoadingAnimation(initialMessage);
}
